import { Theme } from "./types";

export const lightTheme: Theme = {
  name: "Light",
  id: "light",
  description: "Clean and modern light theme",
  isDark: false,

  colors: {
    // Base colors (matched to logo green)
    primary: "#038458",
    primaryHover: "#026E49",
    primaryForeground: "#FFFFFF",

    secondary: "#6B7280",
    secondaryHover: "#4B5563",
    secondaryForeground: "#FFFFFF",

    // Accent tuned to complement primary
    accent: "#05A77A",
    accentHover: "#048C66",
    accentForeground: "#FFFFFF",

    // Semantic colors
    success: "#10B981",
    successHover: "#059669",
    successForeground: "#FFFFFF",

    warning: "#F59E0B",
    warningHover: "#D97706",
    warningForeground: "#FFFFFF",

    danger: "#EF4444",
    dangerHover: "#DC2626",
    dangerForeground: "#FFFFFF",

    // Info shifted to green-turquoise family for harmony
    info: "#0CC28A",
    infoHover: "#06A06F",
    infoForeground: "#06351F",

    // Surface colors
    background: "#FFFFFF",
    backgroundSecondary: "#F9FAFB",
    backgroundTertiary: "#F3F4F6",

    surface: "#FFFFFF",
    surfaceHover: "#F9FAFB",
    surfaceBorder: "#E5E7EB",

    // Text colors
    text: "#111827",
    textSecondary: "#4B5563",
    textMuted: "#6B7280",
    textDisabled: "#9CA3AF",

    // UI element colors
    border: "#E5E7EB",
    borderHover: "#D1D5DB",
    borderFocus: "#038458",

    input: "#FFFFFF",
    inputBorder: "#E5E7EB",
    inputFocus: "#038458",
    inputPlaceholder: "#9CA3AF",

    // Chart colors
    chart1: "#038458",
    chart2: "#06A06F",
    chart3: "#0CC28A",
    chart4: "#65A30D",
    chart5: "#34D399",
    chart6: "#10B981",

    // Segment colors
    segmentGreeting: "#038458",
    segmentPitch: "#06A06F",
    segmentObjection: "#F59E0B",
    segmentAgreement: "#0CC28A",
    segmentClosing: "#65A30D",

    // Scrollbar colors
    scrollbarTrack: "#F3F4F6",
    scrollbarThumb: "#D1D5DB",
    scrollbarThumbHover: "#9CA3AF",
    scrollbarThumbActive: "#6B7280",
    scrollbarCorner: "#F3F4F6",
  },

  typography: {
    fontFamilyBase:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyHeading:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyMono:
      '"SF Mono", Monaco, "Cascadia Mono", "Roboto Mono", Consolas, "Courier New", monospace',
    fontFamilyDisplay:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
    },

    fontWeight: {
      thin: 100,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },

    letterSpacing: {
      tighter: "-0.05em",
      tight: "-0.025em",
      normal: "0em",
      wide: "0.025em",
      wider: "0.05em",
      widest: "0.1em",
    },
  },

  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
    "4xl": "6rem",
    "5xl": "8rem",
  },

  borderRadius: {
    none: "0",
    sm: "0.125rem",
    base: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  },

  shadows: {
    none: "none",
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
    focus: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    focusPrimary: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    focusSecondary: "0 0 0 3px rgba(107, 114, 128, 0.1)",
    focusDanger: "0 0 0 3px rgba(239, 68, 68, 0.1)",
  },

  animations: {
    duration: {
      instant: "0ms",
      fast: "150ms",
      normal: "250ms",
      slow: "350ms",
      slower: "500ms",
    },

    easing: {
      linear: "linear",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      elastic: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    },

    animations: {
      fadeIn: "fadeIn 250ms ease-in-out",
      fadeOut: "fadeOut 250ms ease-in-out",
      slideIn: "slideIn 250ms ease-out",
      slideOut: "slideOut 250ms ease-in",
      bounce: "bounce 1s infinite",
      pulse: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
      spin: "spin 1s linear infinite",
      wiggle: "wiggle 1s ease-in-out infinite",
    },
  },

  components: {
    button: {
      base: "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
      sizes: {
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2 text-base",
        lg: "px-6 py-3 text-lg",
      },
      variants: {
        primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        outline: "border border-border bg-transparent hover:bg-surface-hover",
        ghost: "bg-transparent hover:bg-surface-hover",
        danger: "bg-danger text-danger-foreground hover:bg-danger-hover",
        success: "bg-success text-success-foreground hover:bg-success-hover",
      },
    },

    card: {
      base: "bg-surface border border-border rounded-lg shadow-sm",
      header: "px-6 py-4 border-b border-border",
      body: "px-6 py-4",
      footer: "px-6 py-4 border-t border-border",
    },

    input: {
      base: "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary",
      sizes: {
        sm: "text-sm py-1.5",
        md: "text-base py-2",
        lg: "text-lg py-3",
      },
      states: {
        default: "border-input-border bg-input",
        focus: "border-input-focus ring-2 ring-primary ring-opacity-20",
        error: "border-danger text-danger",
        disabled: "bg-background-secondary cursor-not-allowed opacity-50",
      },
    },

    badge: {
      base: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
      variants: {
        default: "bg-background-secondary text-text-secondary",
        primary: "bg-primary bg-opacity-10 text-primary",
        secondary: "bg-secondary bg-opacity-10 text-secondary",
        success: "bg-success bg-opacity-10 text-success",
        warning: "bg-warning bg-opacity-10 text-warning",
        danger: "bg-danger bg-opacity-10 text-danger",
        info: "bg-info bg-opacity-10 text-info",
      },
    },

    table: {
      base: "w-full border-collapse",
      header: "bg-background-secondary border-b border-border",
      row: "border-b border-border",
      cell: "px-4 py-3 text-left",
      striped: "even:bg-background-secondary",
      hover: "hover:bg-background-secondary",
    },

    modal: {
      overlay: "fixed inset-0 bg-black bg-opacity-50",
      content: "bg-surface rounded-lg shadow-xl",
      header: "px-6 py-4 border-b border-border",
      body: "px-6 py-4",
      footer: "px-6 py-4 border-t border-border",
      closeButton: "text-text-muted hover:text-text",
    },
  },

  effects: {
    gradients: {
      primary: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      secondary: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      accent: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    },

    backdropBlur: {
      none: "blur(0)",
      sm: "blur(4px)",
      base: "blur(8px)",
      md: "blur(12px)",
      lg: "blur(16px)",
    },
  },
};
